import React from "react";
import { MASTER_PRESETS } from "../../lib/studioMaster";

// Master / Mix drawer: master volume + mastering preset (owner) and a
// per-track mix (volume, pan, mute, solo). Clip meter stays on the transport.
const panLabel = (p) => (p === 0 ? "C" : p < 0 ? `L${Math.round(-p * 100)}` : `R${Math.round(p * 100)}`);

const TrackMix = ({ t, editable, onTrack }) => (
  <li className="p-2 rounded-lg bg-black/30 border border-white/10" data-testid={`mix-row-${t.track_id}`}>
    <div className="flex items-center gap-1.5">
      <span className="flex-1 truncate text-[11px] font-bold">{t.label || "Vocal"}</span>
      <button type="button" onClick={() => onTrack(t.track_id, { mute: !t.mute })} data-testid={`mix-mute-${t.track_id}`}
        className={`h-6 px-2 rounded text-[10px] font-black border ${t.mute ? "bg-red-500/80 border-red-300 text-white" : "border-white/15 text-gray-300"}`}>M</button>
      <button type="button" onClick={() => onTrack(t.track_id, { solo: !t.solo })} data-testid={`mix-solo-${t.track_id}`}
        className={`h-6 px-2 rounded text-[10px] font-black border ${t.solo ? "bg-amber-400 border-amber-200 text-black" : "border-white/15 text-gray-300"}`}>S</button>
    </div>
    <div className="grid grid-cols-[1fr_auto_5rem_auto] items-center gap-1.5 mt-1">
      <input type="range" min={-24} max={6} step={0.5} value={t.gain_db || 0} disabled={!editable}
        onChange={(e) => onTrack(t.track_id, { gain_db: parseFloat(e.target.value) })}
        className="accent-fuchsia-400" aria-label="Track volume" data-testid={`mix-gain-${t.track_id}`} />
      <span className="text-[9px] tabular-nums text-gray-400 w-9">{(t.gain_db || 0).toFixed(1)}dB</span>
      <input type="range" min={-1} max={1} step={0.05} value={t.pan || 0} disabled={!editable}
        onChange={(e) => onTrack(t.track_id, { pan: parseFloat(e.target.value) })}
        onDoubleClick={() => editable && onTrack(t.track_id, { pan: 0 })}
        className="accent-sky-400" aria-label="Pan" data-testid={`mix-pan-${t.track_id}`} />
      <span className="text-[9px] tabular-nums text-gray-400 w-7" data-testid={`mix-pan-value-${t.track_id}`}>{panLabel(t.pan || 0)}</span>
    </div>
  </li>
);

export default function MasterDrawer({ open, onClose, preset, intensity, onChange, saving, volume = 1, onVolume,
  isOwner = true, tracks = [], onTrack, canWrite = () => true, beatVolume = 1, onBeatVolume, meter = null }) {
  if (!open) return null;
  const off = preset === "off";
  return (
    <>
      <div className="fixed inset-0 z-30 bg-black/40" onClick={onClose} data-testid="master-scrim" />
      <aside
        className="fixed left-0 right-0 bottom-[76px] z-40 mx-auto max-w-2xl max-h-[70vh] overflow-y-auto rounded-t-2xl bg-[#0d0a18] border border-amber-400/25 shadow-[0_-8px_24px_rgba(0,0,0,0.55)] p-4"
        role="dialog" aria-label="Master and mix" data-testid="studio-master-drawer">
        <header className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm font-black text-amber-300 tracking-wide">Master / Mix</p>
            <p className="text-[11px] text-gray-400">Levels apply to playback and Final Mix.</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white text-xs" data-testid="master-close-btn">Close ✕</button>
        </header>
        <label className="block mb-3">
          <span className="flex items-center justify-between text-[10px] uppercase tracking-widest text-gray-400 font-bold">
            <span>Master volume</span>
            <span className="tabular-nums text-amber-200" data-testid="master-volume-value">{Math.round(volume * 100)}%</span>
          </span>
          <input type="range" min={0} max={1.5} step={0.05} value={volume} onChange={(e) => onVolume?.(parseFloat(e.target.value))}
            className="w-full accent-amber-400 mt-2" data-testid="master-volume-slider" />
        </label>
        <label className="block mb-3">
          <span className="flex items-center justify-between text-[10px] uppercase tracking-widest text-gray-400 font-bold">
            <span>Beat volume</span>
            <span className="tabular-nums text-fuchsia-200" data-testid="mix-beat-volume-value">{Math.round(beatVolume * 100)}%</span>
          </span>
          <input type="range" min={0} max={1.5} step={0.05} value={beatVolume} onChange={(e) => onBeatVolume?.(parseFloat(e.target.value))}
            className="w-full accent-fuchsia-400 mt-2" data-testid="mix-beat-volume-slider" />
        </label>
        {meter && (
          <div className="mb-3 rounded-lg border border-white/10 bg-black/30" data-testid="mix-output-meter">
            <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold px-2 pt-1.5">Output level</p>
            {meter}
          </div>
        )}
        {tracks.length > 0 && (
          <div className="mb-4">
            <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-2">Track mix</p>
            <ul className="space-y-1.5" data-testid="mix-track-list">
              {tracks.map((t) => <TrackMix key={t.track_id} t={t} editable={canWrite(t.track_id)} onTrack={onTrack} />)}
            </ul>
          </div>
        )}
        {isOwner && (<>
          <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-2">Mastering</p>
          <div className="grid grid-cols-5 gap-1.5" role="radiogroup" aria-label="Mastering preset">
            {MASTER_PRESETS.map((p) => (
              <button key={p.key} type="button" role="radio" aria-checked={preset === p.key}
                onClick={() => onChange({ preset: p.key, intensity })}
                className={`h-10 rounded-lg border text-[11px] font-bold transition-colors ${
                  preset === p.key ? "bg-amber-400 border-amber-300 text-black" : "border-white/15 text-gray-200 hover:border-amber-300/50"}`}
                data-testid={`master-preset-${p.key}`}>
                {p.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-gray-400 mt-2 min-h-[16px]" data-testid="master-preset-hint">
            {MASTER_PRESETS.find((p) => p.key === preset)?.hint}
          </p>
          <label className={`block mt-3 ${off ? "opacity-40" : ""}`}>
            <span className="flex items-center justify-between text-[10px] uppercase tracking-widest text-gray-400 font-bold">
              <span>Intensity</span>
              <span className="tabular-nums text-amber-200" data-testid="master-intensity-value">{intensity}%</span>
            </span>
            <input type="range" min={0} max={100} step={5} value={intensity} disabled={off}
              onChange={(e) => onChange({ preset, intensity: parseInt(e.target.value, 10) })}
              className="w-full accent-amber-400 mt-2" data-testid="master-intensity-slider" />
          </label>
          <p className="text-[10px] text-gray-500 mt-2" data-testid="master-save-state">
            {saving ? "Saving…" : "Saved with this session."}
          </p>
        </>)}
      </aside>
    </>
  );
}
